import {
  ChangeDetectionStrategy,
  Component,
  AfterViewInit,
  inject,
  signal,
} from "@angular/core";
import {
  FormsModule,
  ReactiveFormsModule,
  FormGroup,
  FormBuilder,
  FormControl,
  Validators,
  AbstractControl,
  ValidationErrors,
} from "@angular/forms";
import { MatAutocompleteModule } from "@angular/material/autocomplete";
import { MatButtonModule } from "@angular/material/button";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatIconModule } from "@angular/material/icon";
import { MatInputModule } from "@angular/material/input";
import { MatSelectModule } from "@angular/material/select";
import {
  TreeViewModule,
  CheckableSettings,
  TreeItemLookup,
} from "@progress/kendo-angular-treeview";
import { CUI_PARENT_CODE } from "../../../../../constants/sensitivity.constants";
import { toUniqueExpandedLabels } from "../../../../../utilities/sensitivity-label.utils";
import { ISensitivityLabelExpanded } from "../../../../../interfaces/i-sensitivity-label";
import { ApiService } from "../../../../../services/api.service";
import { AgentService } from "../../../../agent/services/agent.service";
import { InstructionBuilderService } from "../services/instruction-builder.service";
import { AgentTableComponent } from "../../../../agent/agent-table/agent-table.component";
import { IAgentsResponse, IMyAgent } from "../../../../../interfaces/i-agents";
import { SpinnerService } from "../../../../../shared/spinner/spinner.service";
import { AlertService } from "../../../../../shared/alert/alert.service";
import { AlertEnum } from "../../../../../shared/alert/alert.enum";
import { UtilitiesService } from "../../../../../services/utilities.service";
import { InstructionType } from "../../../../../enums/automation.enum";
import { SensitivityLabelService } from "../../../../../services/sensitivity-label.service";

@Component({
  selector: "app-instruction-types",
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatButtonModule,
    MatIconModule,
    MatAutocompleteModule,
    TreeViewModule,
    AgentTableComponent,
  ],
  templateUrl: "./instruction-types.component.html",
  styleUrl: "./instruction-types.component.scss",
})
export class InstructionTypesComponent implements AfterViewInit {
  instructionForm!: FormGroup;
  agentsLoaded = signal<boolean>(false);
  gridData: IMyAgent[] = [];
  gridView = signal<IMyAgent[]>([]);
  showExcelExportButton = signal<boolean>(false);
  showActionsColumn = signal<boolean>(false);
  showGoToMostRecentSessionButton = signal<boolean>(false);
  tableTooltipText = signal<string>(
    'Agents are automated processes (eg. APIs and microservices) that are registered for the AI model to utilize when handling requests. For example, the AI will ask the "Search Internet" agent to perform a web search based on a search criteria and return it to the AI. Upon which the AI will attempt to use that information to handle your request.'
  );
  selectableMode = signal<"single" | "multiple">("single");

  instructionPromptInfoText = "The prompt for the instruction.";
  // determines how we populate the selected agent, if any, and the sensitivity labels on initial load
  isInitialLoad = true;

  // Kendo UI TreeView
  expandedKeys: string[] = [];
  checkedKeys: string[] = [];
  filteredSensitivityLabelData = signal<ISensitivityLabelExpanded[]>([]);
  checkableSettings: CheckableSettings = {
    checkChildren: true,
    checkDisabledChildren: true, // Check all children
    checkParents: true,
    enabled: true, // Enable checkable mode
    mode: "multiple", // Multiple selection mode
    checkOnClick: true, // Check all parents when children are checked
    uncheckCollapsedChildren: false, // Uncheck all children when parent is unchecked,
  };

  // Services
  instructionBuilderService = inject(InstructionBuilderService);
  #agentService = inject(AgentService);
  #apiService = inject(ApiService);
  #spinnerService = inject(SpinnerService);
  #alertService = inject(AlertService);
  #utilitiesService = inject(UtilitiesService);
  #sensitivityLabelService = inject(SensitivityLabelService);

  /** Whether the sensitivity-label picker is shown; hidden when the feature is disabled. */
  readonly slEnabled = this.#sensitivityLabelService.enabled;

  // Expose the enum to the template
  readonly InstructionType = InstructionType;

  instructionTypes = [
    { value: InstructionType.AiPrompt, viewValue: InstructionType.AiPrompt },
    { value: InstructionType.Agent, viewValue: InstructionType.Agent },
    {
      value: InstructionType.FileUpload,
      viewValue: InstructionType.FileUpload,
    },
    {
      value: InstructionType.CollectInformation,
      viewValue: InstructionType.CollectInformation,
    },
  ];
  constructor(private fb: FormBuilder) {
    // Initialize the form with default values or values from an instruction.

    this.instructionForm = this.fb.group({
      sensitivityLabels: new FormControl(
        this.instructionBuilderService.instruction().details.sensitivityLabels
      ),
      type: new FormControl(this.instructionBuilderService.instruction().details.type, [
        Validators.required,
      ]),
      prompt: new FormControl(
        this.instructionBuilderService.instruction().details.prompt,
        [Validators.required]
      ),
      agent: new FormControl<string | undefined>(
        this.instructionBuilderService.instruction().details.agent
      ),
      fileUploadLabel: new FormControl<string | undefined>(
        this.instructionBuilderService.instruction().details.fileUploadLabel
      ),
      collectInformationLabel: new FormControl<string | undefined>(
        this.instructionBuilderService.instruction().details.collectInformationLabel
      ),
    });

    // Ensure sensitivity label selections persist across step navigation
    // by syncing `checkedKeys` from the instruction service on load.
    this.updateSensitivityLabelsData();

    this.#apiService.getAgents().subscribe({
      next: (data: IAgentsResponse) => {
        const updatedData = this.#agentService.convertAgentsToArray(data);
        const updatedDataAgents = {
        ...updatedData,
        agents: updatedData.agents
          .filter((item) => item.details) // Filter out items with null or undefined details
          .map((item) => ({
          ...item,
          details: this.#agentService.convertAgentArraysToSting(
            item.details
          ),
          })),
        };
        this.#agentService.agents.set(updatedData);
        this.#agentService.setMyAgentsFromAgents(updatedData);
        this.#agentService.myAgents.set(this.#agentService.myAgents());

        const selectedAgentId = this.instructionBuilderService.instruction().details.agent;
        const agents = this.#agentService.myAgents();
        const selectedAgent = agents.find(agent => agent.id === selectedAgentId);

        this.gridData = selectedAgent
          ? [selectedAgent, ...agents.filter(agent => agent.id !== selectedAgentId)]
          : [...agents];
        this.gridView.set(this.gridData);
        this.agentsLoaded.set(true);
      },
      error: (error: any) => {
        this.#spinnerService.hide();
        this.#alertService.showAlert({
        message: `Failed to get all agents.`,
        type: AlertEnum.error,
        });
      },
      complete: () => {
        this.#spinnerService.hide();
      },
    });
  }

  ngAfterViewInit(): void {
    // Add conditional validators based on the selected type
    this.instructionForm.get("type")?.valueChanges.subscribe((type) => {
      this.updateValidatorsBasedOnType(type);
    });
  }

  // BEGIN Validation for the stepper and form

  /**
   * Updates validators for form fields based on the selected instruction type
   */
  updateValidatorsBasedOnType(type: string): void {
    // Reset the prepended prompt field value
    this.instructionForm.get("prependedPrompt")?.setValue("");
    // Reset all type-specific field validators
    this.instructionForm.get("fileUploadLabel")?.clearValidators();
    this.instructionForm.get("collectInformationLabel")?.clearValidators();

    // Set validators based on type
    // We skip AI Prompt as it has no unique field requirements.
    switch (type) {
      case InstructionType.Agent:
        this.instructionPromptInfoText =
          "The text 'Use the [x] agent.' will be prepended to the prompt you enter below.";
        break;
      case InstructionType.FileUpload:
        this.instructionPromptInfoText =
          "The text 'Use the Read CGI Federal File agent to review the file.' will be prepended to the prompt you enter below.";
        this.instructionForm
          .get("fileUploadLabel")
          ?.setValidators([Validators.required]);
        // Handle the async method by using a Promise
        this.instructionBuilderService.getReadCgiFederalFileAgentId()
          .then((readCgiFederalAgentId) => {
            this.instructionForm.get("agent")?.setValue(readCgiFederalAgentId);
            // Also update the instruction object to ensure consistency
            this.instructionBuilderService.instruction.update(prev => ({
              ...prev,
              details: {
                ...prev.details,
                agent: readCgiFederalAgentId
              }
            }));
          })
          .catch((error) => {
            this.#alertService.showAlert({
              message: `Failed to get Read CGI Federal File agent ID.`,
              type: AlertEnum.error,
            });
          });
        break;
      case InstructionType.CollectInformation:
        this.instructionForm
          .get("collectInformationLabel")
          ?.setValidators([Validators.required]);

        this.instructionPromptInfoText = "The prompt entered here will be prepended to the user's prompt entered in the collect information input.";
        break;
      default:
        this.instructionPromptInfoText = "The prompt for the instruction.";
    }

    // Update validity
    this.instructionForm.get("keywords")?.updateValueAndValidity();
    this.instructionForm.get("name")?.updateValueAndValidity();
    this.instructionForm.get("model")?.updateValueAndValidity();
    this.instructionForm.get("temperature")?.updateValueAndValidity();
    this.instructionForm.get("topP")?.updateValueAndValidity();
    this.instructionForm.get("instructionPrompt")?.updateValueAndValidity();
    this.instructionForm.get("fileUploadLabel")?.updateValueAndValidity();
    this.instructionForm.get("prompt")?.updateValueAndValidity();
    this.instructionForm
      .get("collectInformationLabel")
      ?.updateValueAndValidity();
    this.instructionForm.get("sensitivityLabels")?.updateValueAndValidity();
  }

  /**
   * Marks all controls in the instruction form as touched.
   * This is useful for triggering validation messages on all fields,
   * typically when the user attempts to submit the form.
   *
   * This is called programmatically in the validateAllChildForms method in the InstructionBuilderComponent.
   */
  markFormAsTouched(): void {
    this.instructionForm.markAllAsTouched();
  }

  /**
   * Validates the form and returns whether it's valid or not.
   * Used by the parent component to determine if this step is complete.
   * @returns A boolean indicating whether the form is valid.
   */
  isValid(): boolean {
    if (!this.instructionForm) {
      return false;
    }

    // Reset form errors
    this.instructionForm.get("agent")?.setErrors(null);
    this.instructionForm.get("sensitivityLabels")?.setErrors(null);

    // Mark all form fields as touched to trigger validation
    this.instructionForm.markAllAsTouched();

    // Get the current instruction type
    const instructionType = this.instructionForm.get('type')?.value;

    // Validate form based on the instruction type
    if (!this.instructionForm.valid && !!this.instructionForm.get('prompt')?.value) {
      // Check if the prompt is valid
      return false;
    } else if (instructionType === InstructionType.Agent) {
      // Check if agent selections are valid
      if (!this.instructionBuilderService.instruction().details.agent) {
        this.instructionForm.get("agent")?.setErrors({ required: true });
        return false;
      } else if (this.slEnabled() && this.checkedKeys.length === 0) {
        // Sensitivity labels are only required when the feature is enabled; otherwise the picker is hidden.
        this.instructionForm.get("sensitivityLabels")?.setErrors({ required: true });
        return false;
      }
      return this.instructionForm.valid && (!this.slEnabled() || this.checkedKeys.length > 0) && this.instructionBuilderService.instruction().details.agent !== "";
    } else if (instructionType === InstructionType.FileUpload) {
      // Check if file upload settings are valid
      return this.instructionForm.valid && !!this.instructionForm.get('fileUploadLabel')?.value;
    } else if (instructionType === InstructionType.CollectInformation) {
      // Check if collect information settings are valid
      return this.instructionForm.valid && !!this.instructionForm.get('collectInformationLabel')?.value;
    }

    return this.instructionForm.valid;
  }

  // BEGIN methods for handling sensitivity labels
  /**
   * Decides whether the checkbox should be displayed for the item.
   * CUI parent and its children are hidden from direct selection.
   */
  hasCheckbox = (item: object, _index: string): boolean => {
    const label = item as ISensitivityLabelExpanded;
    return !(label.parent === CUI_PARENT_CODE || label.id === CUI_PARENT_CODE);
  };

  onSelectionChange(event: TreeItemLookup): void {
    this.instructionForm
      .get("sensitivityLabels")
      ?.setValue([...this.checkedKeys]);
    this.instructionForm.get("sensitivityLabels")?.markAsTouched();
    this.instructionForm.get("sensitivityLabels")?.updateValueAndValidity();
    this.instructionBuilderService.instruction().details.sensitivityLabels = this.#utilitiesService.findLabelsByCodes(this.checkedKeys);
  }

  /**
   * Method to update the checkbox state from the parent component
   */
  updateCheckedKeys(checkedKeys: any[]): void {
    this.checkedKeys = checkedKeys;
    this.instructionForm.get("sensitivityLabels")?.setValue(checkedKeys);
    if (this.checkedKeys.length === 0) {
      this.instructionForm.get("sensitivityLabels")?.setErrors({ required: true });
    }
    // Persist to the instruction service so selections survive step changes
    this.instructionBuilderService.instruction().details.sensitivityLabels = this.#utilitiesService.findLabelsByCodes(this.checkedKeys);
  }

  isSensitivityLabelDisabled = (): boolean => {
    return false;
  };

  /**
   * Validator for sensitivity labels
   */
  sensitivityLabelValidator(control: AbstractControl): ValidationErrors | null {
    const value = control.value;
    if (!value || value.length === 0) {
      return { required: true };
    }
    return null;
  }

  /**
   * Synchronizes both sensitivityLabelData and checkedKeys with the current instruction's
   * sensitivityLabels while preserving icon values.
   * This now also updates the filteredSensitivityLabelData signal.
   */
  updateSensitivityLabelsData(): void {
    // Map backend ISensitivityLabel objects to GUID strings for the TreeView.
    const instructionLabelGuids = toUniqueExpandedLabels(
      this.instructionBuilderService.instruction().details.sensitivityLabels
    ).map(l => l.id);

    // Update checkedKeys for the TreeView component
    this.checkedKeys = [...instructionLabelGuids];

    // We grab the agent from the gridData based on the agent ID in the instruction.
    // This is to ensure we are filtering based on the agent's sensitivity labels so we can
    // set the min and max label orders correctly.
    const agent = this.gridData.filter(
      (agent) => agent.id === this.instructionBuilderService.instruction().details.agent
    );

    const agentLabelGuids = toUniqueExpandedLabels(agent[0]?.sensitivityLabels).map(l => l.id);

    // Update the filtered data signal
    const allLabels = this.#sensitivityLabelService.labels();
    if (agentLabelGuids.length === 0 || this.instructionBuilderService.instruction().details.type !== InstructionType.Agent) {
      this.filteredSensitivityLabelData.set(allLabels);
    } else {
      // Find the min and max sensitivity values for the agent's label range
      const agentOrders = agentLabelGuids
        .map(code => allLabels.find(l => l.id === code)?.sensitivity ?? 0)
        .filter(o => o > 0);
      const minOrder = Math.min(...agentOrders);
      const maxOrder = Math.max(...agentOrders);

      const filtered = allLabels.filter(
        (label) =>
          agentLabelGuids.includes(label.id) ||
          // Include parent categories if they have selected children
          allLabels.some(
            (child) =>
              child.parent === label.id &&
              agentLabelGuids.includes(child.id)
          ) ||
          // Include all labels with sensitivity between the min and max selected
          (label.sensitivity >= minOrder && label.sensitivity <= maxOrder)
      );
      this.filteredSensitivityLabelData.set(filtered);
    }

    // If the form exists, update the form control as well for consistency
    if (this.instructionForm) {
      this.instructionForm
        .get("sensitivityLabels")
        ?.setValue(this.checkedKeys);
    }
  }

  // END methods for handling sensitivity labels

  /**
   * Resets the form fields when the instruction type changes.
   * This is useful to clear fields that are not relevant for the new type.
   * We leave the sensitivityLabels, name, model, temperature, and topP fields as they are common across types.
   */
  resetFieldsOnTypeChange(): void {
    const currentType = this.instructionForm.get("type")?.value;

    // Update checkedKeys with the instruction's sensitivity labels resolved to GUIDs.
    this.checkedKeys = toUniqueExpandedLabels(
      this.instructionBuilderService.instruction().details.sensitivityLabels
    ).map(l => l.id);

    // Reset type-specific fields
    if (currentType === InstructionType.Agent) {
      this.instructionBuilderService.instruction().details.sensitivityLabels = [];
      this.instructionForm.get("sensitivityLabels")?.reset();
    } else {
      this.instructionBuilderService.instruction().details.fileUploadLabel = "";
      this.instructionBuilderService.instruction().details.collectInformationLabel = "";
      this.instructionBuilderService.instruction().details.agent = "";
      this.instructionForm.get("sensitivityLabels")?.setValue(this.instructionBuilderService.instruction().details.sensitivityLabels);
      this.filteredSensitivityLabelData.set(this.#sensitivityLabelService.labels());
    }

    this.instructionForm.get("prependedPrompt")?.reset();
    this.instructionForm.get("agent")?.reset();
    this.instructionForm.get("fileUploadLabel")?.reset();
    this.instructionForm.get("collectInformationLabel")?.reset();

    // Make sure to update validators
    this.updateValidatorsBasedOnType(currentType);
  }

  /**
   * Updates the instruction with the selected agents and triggers
   * the sensitivity labels update.
   * This method is called when the agent selection changes in the agent-table.
   *
   * @param agents - The array of selected agents
   */
  updateInstructionWithAgents(agents: IMyAgent[]): void {
    // Update the instruction with the selected agents
    if (this.instructionBuilderService.instruction().details.agent && this.isInitialLoad) {
      this.isInitialLoad = false;
    } else {
      this.instructionBuilderService.instruction.update(prev => ({
        ...prev,
        details: {
          ...prev.details,
          agent: agents.length > 0 ? agents[0].id : '',
          sensitivityLabels: agents.length > 0 ? agents[0].sensitivityLabels : []
        }
      }));
    }

    // Then update sensitivity labels data
    this.updateSensitivityLabelsData();
  }
}
